import { Component, Input, Output, EventEmitter, NgZone, ChangeDetectorRef, OnDestroy, HostBinding, ElementRef, ViewChild, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import { EnpsComponent } from '@common/components/base/enps-component';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { IEquals } from '@common/interfaces/i-equals';
import { AppService } from '@suite/app.service';

import { GlobalTablesService } from '@common/services/global-tables-service';
import { BooleanInput, NumberInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { Moment } from 'moment-timezone';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatChipGrid } from '@angular/material/chips';
import { MatSelect } from '@angular/material/select';
import { Subject } from 'rxjs';

type Direction = 'rtl' | 'ltr';

@Component({
    template: ""
})
export class EnpsControl<T extends IEquals<T> | string | number | boolean | Array<any> | Date | Moment> extends EnpsComponent implements OnDestroy, AfterViewInit {

    constructor(
        public appService: AppService,
        protected ngZone: NgZone,
        protected changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        public globalTablesService: GlobalTablesService,
        protected elmRef: ElementRef,
    ) {
        super(timeoutQueue);
    }

    @ViewChild('searchBox') searchBox: ElementRef<HTMLInputElement>;

    @Output() public changed = new EventEmitter<any>();

    @ViewChildren(MatFormFieldControl) internalControl: QueryList<MatFormFieldControl<MatInput | MatChipGrid | MatSelect>>;
    destroyed$ = new Subject<void>();

    @HostBinding('attr.dir')
    get dir(): Direction {
        return this.globalTablesService.getRtl();
    }

    get locale(): string {
        if(this.appService.nomWebServicesJson.HelloResults.PersonalLanguage) {
            return this.appService.nomWebServicesJson.HelloResults.PersonalLanguage.replace(/(..)$/, "-$1")
        } else if (this.appService.visualStateService.ConfigSettings.BaseLanguage) {
            if (this.appService.visualStateService.ConfigSettings.BaseLanguage.length > 2) {
                return this.appService.visualStateService.ConfigSettings.BaseLanguage.replace(/(..)$/, "-$1")
            }
            return this.appService.visualStateService.ConfigSettings.BaseLanguage
        } else {
            return window.navigator.languages[0];
        }
    }

    protected _value: T;
    @Input()
    public get value(): T {
        return this._value;
    }
    public set value(v: T) {
        this._value = v;
        this._syncValueIfNecessary();
    }

    private _placeholder: string;
    @Input()
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(plh: string) {
        this._placeholder = plh;
    }

    @Input()
    get disabled() {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        if (this._disabled) {
            this._setDisabledState(true);
        } else {
            this._setDisabledState(false);
        }
    }
    private _disabled = false;

    private _label: string = '';
    @Input()
    get label(): string {
        return this._label;
    }
    set label(value: string) {
        this._label = value;
    }

    private _hint: string = '';
    @Input()
    get hint(): string {
        return this._hint;
    }
    set hint(value: string) {
        this._hint = value;
    }

    private _error: string = '';
    @Input()
    get error(): string {
        return this._error;
    }
    set error(value: string) {
        this._error = value;
    }

    @Input()
    get required() {
        return this._required;
    }
    set required(req: BooleanInput) {
        this._required = coerceBooleanProperty(req);
        if (this._required) {
            this._updateInvalidState();
        }
    }

    private _appearance: string = 'outline';
    @Input()
    get appearance(): string {
        return this._appearance;
    }
    set appearance(value: string) {
        this._appearance = value;
    }

    @Input()
    get hasPrefix() {
        return this._hasPrefix;
    }
    set hasPrefix(value: BooleanInput) {
        this._hasPrefix = coerceBooleanProperty(value);
    }
    private _hasPrefix = false;

    @Input()
    get hasSuffix() {
        return this._hasSuffix;
    }
    set hasSuffix(value: BooleanInput) {
        this._hasSuffix = coerceBooleanProperty(value);
    }
    private _hasSuffix = false;

    @Input()
    get hasError() {
        return this._hasError;
    }
    set hasError(value: BooleanInput) {
        this._hasError = coerceBooleanProperty(value);
    }
    private _hasError = false;

    @Input()
    get asCustomField() {
        return this._asCustomField;
    }
    set asCustomField(value: BooleanInput) {
        this._asCustomField = coerceBooleanProperty(value);
    }
    private _asCustomField = false;

    @Input()
    get maxLength() {
        return this._maxLength;
    }
    set maxLength(value: NumberInput) {
        this._maxLength = coerceNumberProperty(value);
    }
    private _maxLength: number = 524288;

    protected isValueChanged: boolean;

    ngAfterViewInit() {
        this._updateInvalidState();
    }


    ngOnDestroy() {
        super.ngOnDestroy();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private _updateInvalidState() {
        if (this.required) {
            this.internalControl?.forEach(c => c.ngControl?.control?.markAllAsTouched());
            this._addValidatorIfNecessary();
        }
    }

    protected _addValidatorIfNecessary() { }
    protected _syncValueIfNecessary() { }
    protected _setDisabledState(value: boolean) { }

    public onMenuOpened() {
        if (this.searchBox) {
            this.searchBox.nativeElement.focus();
        }
    }
}
